import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { HlmAlert } from '@lite-workspace/shared/helm-ui/alert';
import { IS_MOBILE, injectOnline } from '@lite-workspace/utils';

/**
 * Один баннер связи на все экраны клиента: под шапкой, во всю ширину, только с телефона.
 * Корень приложения монтирует его один раз и задаёт `routes` — адреса, на которых баннер живёт.
 */
@Component({
  selector: 'hlm-client-offline-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmAlert],
  template: `
    @if (shown()) {
      <!-- Под шапкой (высоту --toph публикует она сама), во всю ширину.
           z-30 — ниже полноэкранных слоёв (просмотр, кадрирование, лист 100 %), они закрывают его сами. -->
      <div
        hlmAlert
        variant="warn"
        class="fixed inset-x-0 top-[var(--client-offline-banner-top)] z-30 rounded-none border-x-0 border-t-0"
      >
        <span>Нет связи — проверьте интернет</span>
        @if (caption()) {
          <span>{{ caption() }}</span>
        }
      </div>
    }
  `,
  styles: `
    /* Баннер fixed, поэтому хост не должен занимать слот во флекс-колонке корня. */
    :host {
      display: contents;

      /* Отступ офлайн-баннера — один токен: шапка плюс живая высота полосы предпросмотра, которую
         публикует сама полоса. Сумма живёт здесь, а не в утилите: в квадратных скобках класса
         допустима ровно одна переменная, без calc. */
      --client-offline-banner-top: calc(var(--inset-under-header) + var(--client-preview-banner-h, 0px));
    }
  `,
})
export class ClientOfflineBannerComponent {
  private readonly router = inject(Router);
  private readonly isMobile = inject(IS_MOBILE);
  private readonly online = injectOnline();

  /** Адреса клиента, на которых баннер показывается. */
  readonly routes = input.required<RegExp>();

  /** Вторая строка — подсказка по текущему экрану. Пусто = одна строка. */
  readonly caption = input('');

  // Адрес после каждой навигации: баннер живёт только на экранах клиента.
  private readonly url = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly shown = computed(() => this.isMobile() && !this.online() && this.routes().test(this.url()));
}
